import React, { useState, useMemo, useEffect, useRef } from 'react'

import { Autocomplete, TextField, Tooltip, InputAdornment, IconButton, Popover } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

import AppLayout from '../../components/Layouts/AppLayout'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import Table from '../../components/forcasting/table.jsx'
import { useAuth } from '../../hooks/auth'
import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import { isValid } from '../../helpers/helper'
import { buildUrl } from '../../helpers/apiHelper'

const searchInputSx = {
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    background: '#F5F5F5',
    borderRadius: '6px',
}

const Forecasts = () => {

    const { user } = useAuth({ middleware: 'guest' })
    const navigate = useNavigate()
    const { company_id, choosesite, setChoosesite, config, pusher } = useStateContext()

    const [isLoading, setIsLoading] = useState(false)
    const [forecasts, setForecasts] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [random, setRandom] = useState(null)

    /* filter options */
    const [customers, setCustomers] = useState([])
    const [items, setItems] = useState([])

    /* filters - restored from localStorage so they survive leaving/reopening this page */
    const [filterCustomerId, setFilterCustomerId] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_customer_id || ''
        }
        return ''
    })
    const [filterCustomerName, setFilterCustomerName] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_customer_name || ''
        }
        return ''
    })
    const [filterItemId, setFilterItemId] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_item_id || ''
        }
        return ''
    })
    const [filterItemName, setFilterItemName] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_item_name || ''
        }
        return ''
    })
    const [filterPeriodFrom, setFilterPeriodFrom] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_period_from ? dayjs(filter[0].forecast_period_from) : null
        }
        return null
    })
    const [filterPeriodTo, setFilterPeriodTo] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.forecast_period_to ? dayjs(filter[0].forecast_period_to) : null
        }
        return null
    })
    const [periodAnchorEl, setPeriodAnchorEl] = useState(null)
    const isInitialSiteEffect = useRef(true)
    const isInitialCustomerEffect = useRef(true)

    /* site follows the global site chosen in the navigation, same as Quotes/Routings */
    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    /* site changed -> reset & refilter customers (and clear item/customer selection) */
    useEffect(() => {
        if (isValid(company_id) && isValid(choosesite)) {
            getCustomers(company_id, choosesite)
        } else {
            setCustomers([])
        }

        if (isInitialSiteEffect.current) {
            isInitialSiteEffect.current = false
        } else {
            setFilterCustomerId('')
            setFilterCustomerName('')
            setPage(1)
        }
    }, [choosesite])

    /* customer changed -> reset & refilter items based on this site+customer combo's forecasts */
    useEffect(() => {
        if (isValid(choosesite) && isValid(filterCustomerId)) {
            getForecastItemOptions(choosesite, filterCustomerId)
        } else {
            setItems([])
        }

        if (isInitialCustomerEffect.current) {
            isInitialCustomerEffect.current = false
        } else {
            setFilterItemId('')
            setFilterItemName('')
            setPage(1)
        }
    }, [filterCustomerId])

    /* persist filters to localStorage so they survive leaving/reopening this page */
    const createFilterArray = (customerId, customerName, itemId, itemName, periodFrom, periodTo) => {
        if (localStorage.getItem('filters') === null) {
            localStorage.setItem('filters', JSON.stringify([{}]))
        }

        const filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].forecast_customer_id = customerId
        filters[0].forecast_customer_name = customerName
        filters[0].forecast_item_id = itemId
        filters[0].forecast_item_name = itemName
        filters[0].forecast_period_from = periodFrom ? dayjs(periodFrom).format('YYYY-MM-DD') : ''
        filters[0].forecast_period_to = periodTo ? dayjs(periodTo).format('YYYY-MM-DD') : ''

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    useEffect(() => {
        createFilterArray(filterCustomerId, filterCustomerName, filterItemId, filterItemName, filterPeriodFrom, filterPeriodTo)
    }, [filterCustomerId, filterCustomerName, filterItemId, filterItemName, filterPeriodFrom, filterPeriodTo])

    useEffect(() => {
        if (isValid(choosesite)) {
            getForecasts()
        }
    }, [choosesite, filterCustomerId, filterItemId, filterPeriodFrom, filterPeriodTo, page, rowsPerPage, random])

    /* real-time updates: any create/update/delete of a forecast (forecast items are saved as part of the forecast) refreshes the list for everyone */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')

        const channelCreated = pusher.subscribe(`${clientId}-forecast-created`)
        const channelUpdated = pusher.subscribe(`${clientId}-forecast-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-forecast-deleted`)

        channelCreated.bind(`${clientId}-forecast-created-event`, data => {
            setRandom(Math.random())
        })

        channelUpdated.bind(`${clientId}-forecast-updated-event`, data => {
            setRandom(Math.random())
        })

        channelDeleted.bind(`${clientId}-forecast-deleted-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-forecast-created`)
            pusher.unsubscribe(`${clientId}-forecast-updated`)
            pusher.unsubscribe(`${clientId}-forecast-deleted`)
        })
    }, [])

    const getForecasts = async () => {
        setIsLoading(true)

        const url = buildUrl(
            '/api/forecasts',
            {
                where: {
                    'site_id': choosesite,
                    'customer_id': filterCustomerId,
                    'item_id': filterItemId,
                    'period_from': filterPeriodFrom ? dayjs(filterPeriodFrom).format('YYYY-MM-DD') : '',
                    'period_to': filterPeriodTo ? dayjs(filterPeriodTo).format('YYYY-MM-DD') : '',
                    'page': page,
                    'page_size': rowsPerPage
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                setTotal(res.data?.total ?? 0)
                setForecasts(res.data?.data ?? [])
            })
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false))
    }

    const getCustomers = async (companyId, siteId) => {
        await axios
            .get(`/api/customers?company_id=${companyId}&blocked=0&location_id=${siteId}`, config)
            .then(res => {
                setCustomers(res.data)
            })
            .catch(({ response }) => console.error(response))
    }

    /* items available to filter by = items that belong to some forecast of the selected site+customer combo */
    const getForecastItemOptions = async (siteId, customerId) => {
        const url = buildUrl(
            '/api/forecasts',
            {
                where: {
                    'site_id': siteId,
                    'customer_id': customerId,
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                const forecastItems = (res.data?.data ?? []).flatMap(forecast => forecast.forecast_items ?? [])
                const uniqueItems = Array.from(new Map(forecastItems.map(item => [item.item_id, item])).values())
                setItems(uniqueItems.map(item => ({ id: item.item_id, item_no: item.item_no, description: item.description })))
            })
            .catch((err) => console.error(err))
    }

    const optionsCustomers = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))
    const optionsItems = items?.map(option => ({ label: option.item_no + ' - ' + option.description, id: option.id }))

    const handleCustomerChange = (a, b) => {
        setFilterCustomerId(b?.id || '')
        setFilterCustomerName(b?.label || '')
        setPage(1)
    }

    const handleItemChange = (a, b) => {
        setFilterItemId(b?.id || '')
        setFilterItemName(b?.label || '')
        setPage(1)
    }

    const handlePeriodFromChange = (newValue) => {
        setFilterPeriodFrom(newValue)
        setPage(1)
    }

    const handlePeriodToChange = (newValue) => {
        setFilterPeriodTo(newValue)
        setPage(1)
    }

    const clearPeriodFilter = (e) => {
        e.stopPropagation()
        setFilterPeriodFrom(null)
        setFilterPeriodTo(null)
        setPage(1)
    }

    const periodLabel = (filterPeriodFrom || filterPeriodTo)
        ? `${filterPeriodFrom ? dayjs(filterPeriodFrom).format('MM/DD/YYYY') : '...'} - ${filterPeriodTo ? dayjs(filterPeriodTo).format('MM/DD/YYYY') : '...'}`
        : ''

    const displayedForecasts = useMemo(() => {
        return forecasts.filter((forecast) => {
            if (filterItemId && !(forecast.forecast_items ?? []).some((fi) => fi.item_id === filterItemId)) {
                return false
            }

            if (filterPeriodFrom && dayjs(forecast.period_to).isBefore(dayjs(filterPeriodFrom), 'day')) {
                return false
            }

            if (filterPeriodTo && dayjs(forecast.period_from).isAfter(dayjs(filterPeriodTo), 'day')) {
                return false
            }

            return true
        })
    }, [forecasts, filterItemId, filterPeriodFrom, filterPeriodTo])

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>

                <div className='p-6 flex w-full items-start justify-between gap-5 max-[1830px]:flex-col'>
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#EEEFF2]'>
                            <div className='flex gap-2 items-center'>
                                <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('forecasts_list')}</p>
                            </div>
                            <div className='flex gap-5 items-center'>
                                {['master_admin', 'office_manager'].includes(user?.role) ? (
                                    <Tooltip disableInteractive title={t('create_new_forecast')} placement='bottom'>
                                        <div>
                                            <AddButton onClick={() => navigate('create')}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                        </div>
                                    </Tooltip>
                                ) : (
                                    ''
                                )}
                            </div>
                        </div>
                        <div className='p-5 border-b border-[#EEEFF2]'>
                            <div className='flex-row flex justify-between items-end w-full gap-3'>
                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        disabled={!isValid(choosesite)}
                                        id="forecasts-filter-customer-autocomplete"
                                        options={optionsCustomers}
                                        value={filterCustomerId ? { label: filterCustomerName, id: filterCustomerId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleCustomerChange}
                                        sx={searchInputSx}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('search_by_customer')}
                                            />
                                        )}
                                    />
                                </div>

                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        disabled={!isValid(choosesite) || !isValid(filterCustomerId)}
                                        id="forecasts-filter-item-autocomplete"
                                        options={optionsItems}
                                        value={filterItemId ? { label: filterItemName, id: filterItemId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleItemChange}
                                        sx={searchInputSx}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={t('search_by_item')}
                                            />
                                        )}
                                    />
                                </div>

                                <div className='w-full'>
                                    <TextField
                                        fullWidth
                                        placeholder={t('search_by_forecast_period')}
                                        value={periodLabel}
                                        onClick={(e) => setPeriodAnchorEl(e.currentTarget)}
                                        sx={{ ...searchInputSx, cursor: 'pointer', '.MuiInputBase-input': { cursor: 'pointer' } }}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {periodLabel ? (
                                                        <IconButton size='small' onClick={clearPeriodFilter}>
                                                            <ClearIcon sx={{ color: '#A0AEC0' }} />
                                                        </IconButton>
                                                    ) : (
                                                        <SearchIcon sx={{ color: '#A0AEC0' }} />
                                                    )}
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <Popover
                                        open={Boolean(periodAnchorEl)}
                                        anchorEl={periodAnchorEl}
                                        onClose={() => setPeriodAnchorEl(null)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    >
                                        <div className='p-4 flex gap-4'>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <div>
                                                    <p className='text-[14px] text-[#718096] mb-1'>{t('start_date')}</p>
                                                    <MobileDatePicker
                                                        value={filterPeriodFrom}
                                                        maxDate={filterPeriodTo || undefined}
                                                        onChange={handlePeriodFromChange}
                                                        slotProps={{ field: { clearable: true, onClear: () => handlePeriodFromChange(null) } }}
                                                    />
                                                </div>
                                                <div>
                                                    <p className='text-[14px] text-[#718096] mb-1'>{t('end_date')}</p>
                                                    <MobileDatePicker
                                                        value={filterPeriodTo}
                                                        minDate={filterPeriodFrom || undefined}
                                                        onChange={handlePeriodToChange}
                                                        slotProps={{ field: { clearable: true, onClear: () => handlePeriodToChange(null) } }}
                                                    />
                                                </div>
                                            </LocalizationProvider>
                                        </div>
                                    </Popover>
                                </div>
                            </div>
                        </div>
                        <div>
                            <Table
                                items={displayedForecasts}
                                total={total}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                opc={setPage}
                                orppc={setRowsPerPage}
                                onDeleted={getForecasts}
                            />
                        </div>
                    </div>
                </div>

            </AppLayout>
        </>
    )
}

export default Forecasts
